/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { forwardRef } from 'react';
import * as React from 'react';

import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';
import type { TrackingData } from 'js/lib/retracked';

import { Grid, Hidden, ProductCard, Typography2, VisuallyHidden, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import Salary from 'bundles/browse-common/components/Salary';
import type { CareerRoleType } from 'bundles/unified-common/types/careerRoles';

import _t from 'i18n!nls/browse-common';

const styles = {
  container: css`
    height: 100%;
    cursor: pointer;

    .cds-CommonCard-title {
      ${typography2.subtitleLarge};
      margin-bottom: var(--cds-spacing-50);

      ${breakpoints.down('xs')} {
        ${typography2.subtitleMedium};
      }
    }

    .cds-ProductCard-listPreviewImage {
      align-self: center;
    }

    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      min-width: unset;

      ${breakpoints.down('xs')} {
        border-width: 1px;
      }
    }

    .cds-ProductCard-content {
      gap: 0 !important;
    }
  `,
  containerDesktop: css`
    .cds-ProductCard-base {
      overflow: hidden;
    }

    .cds-ProductCard-gridCard {
      flex-direction: row-reverse;
    }

    .cds-ProductCard-gridCard .cds-ProductCard-gridPreviewContainer {
      z-index: -1;
      max-width: unset;
      display: flex;
      flex: 0.5;
      align-items: flex-end;
    }

    .cds-ProductCard-gridCard .cds-ProductCard-gridPreviewContainer .cds-CommonCard-previewImage {
      position: relative;
      right: -24px;
      margin-left: -100px;
      margin-bottom: -8px;
      margin-right: -100px;
      ${breakpoints.down('lg')} {
        margin-left: -80px;
      }
      ${breakpoints.down('md')} {
        margin-left: -70px;
      }
    }
  `,
  containerMobile: css`
    .cds-ProductCard-listContentContainer {
      flex-direction: row;
    }
  `,
  bodyContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    strong {
      color: var(--cds-color-neutral-primary);
    }

    h3 {
      ${typography2.bodySecondary};
      font-weight: bold;
    }
  `,
  offeredBy: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  logo: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-50);
    padding: var(--cds-spacing-50);
  `,
};

type Pathways = CareerRoleType['pathways'];
type Partner = { name: string; logo: string };

function collectUniquePartners(pathways: Pathways) {
  const partnerMap = new Map<string, Partner>();
  pathways?.entities?.forEach((pathway) => {
    pathway.partners?.forEach((partner) => {
      if (!partnerMap.has(partner.name) && partner.squareLogo) {
        partnerMap.set(partner.name, { name: partner.name, logo: partner.squareLogo });
      }
    });
  });
  return Array.from(partnerMap.values());
}

type Props = CareerRoleType & {
  proficiencyLevel?: PathwayLevel;
  showPathwaysLoadingState?: boolean;
  eventingData?: TrackingData;
};

/**
 * @description CareerRoleCard is a component that displays a card that represents a career role.
 */
const CondensedCareerRoleCard = forwardRef<HTMLDivElement, Props>(
  (
    { name: roleName, shortDescription, roleCardImage, jobPostingData, pathways, slug, eventingData, proficiencyLevel },
    ref
  ) => {
    const track = useTracker();

    const { medianSalary, currencyCode } = jobPostingData ?? {};

    const careerCardEventingV3Data = {
      ...eventingData,
      role: {
        roleSlug: slug,
      },
    };

    const visibilityRef = useVisibilityTracker('view_role_card', careerCardEventingV3Data);
    const combinedRef = (node: HTMLDivElement | null) => {
      (visibilityRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    };

    if (!slug) {
      return null;
    }

    const onSelectCard = () => {
      track('click_role_card', careerCardEventingV3Data);
    };

    const recommenderId = eventingData?.carousel?.id;

    const createHref = (roleSlug: string) => {
      const searchParams = new URLSearchParams();

      if (proficiencyLevel) {
        searchParams.set('level', proficiencyLevel.toLowerCase());
      }

      if (recommenderId) {
        searchParams.set('recommenderId', recommenderId);
      }

      const queryString = searchParams.toString();
      return `/career-academy/roles/${roleSlug}${queryString ? `?${queryString}` : ''}`;
    };

    const commonProductCardProps = {
      ref: combinedRef,
      productType: 'role' as const,
      renderPreviewImage: () => (roleCardImage?.url ? <Imgix src={roleCardImage.url} alt="" maxWidth={420} /> : null),
      title: {
        name: roleName ?? '',
        linkProps: {
          href: createHref(slug),
          tabIndex: -1, // title should not be focusable
        },
      },
      onKeyPress: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          onSelectCard();
        }
      },
      onClick: onSelectCard,
      tabIndex: 0,
    };

    const partners = collectUniquePartners(pathways).slice(0, 3);

    return (
      <React.Fragment>
        <Hidden smUp css={[styles.container, styles.containerMobile]}>
          <ProductCard
            {...commonProductCardProps}
            variant="list"
            body={
              <div css={styles.bodyContainer}>
                {!!medianSalary && currencyCode && (
                  <Salary medianSalary={medianSalary} currencyCode={currencyCode} emphasis={false} />
                )}
              </div>
            }
          />
        </Hidden>
        <Hidden only="xs" css={[styles.container, styles.containerDesktop]}>
          <ProductCard
            {...commonProductCardProps}
            variant="grid"
            body={
              <div css={styles.bodyContainer}>
                <VisuallyHidden component="span">{_t('description:')}</VisuallyHidden>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {shortDescription}
                </Typography2>
                <div>
                  {!!medianSalary && currencyCode && (
                    <Salary medianSalary={medianSalary} currencyCode={currencyCode} emphasis={true} />
                  )}
                </div>
                {partners && partners.length > 0 && (
                  <div>
                    <Typography2 variant="actionSecondary" color="body" component="p" css={styles.offeredBy}>
                      {_t('Offered by')}
                    </Typography2>
                    <Grid container spacing={4}>
                      {partners.map((partner) => (
                        <Grid item key={partner.logo}>
                          <Imgix
                            maxHeight={24}
                            maxWidth={24}
                            src={partner.logo}
                            alt={`${partner.name}_logo`}
                            css={styles.logo}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                )}
              </div>
            }
          />
        </Hidden>
      </React.Fragment>
    );
  }
);

export default CondensedCareerRoleCard;
